/*
 * BIC des banques belges, déduit des 3 premiers chiffres du numéro de compte (positions 5 à 7 de l'IBAN).
 * Source : liste des codes d'identification bancaire de la Banque nationale de Belgique,
 * telle que reprise par le paquet npm « iban-to-bic » 1.4.0 (licence MIT, © 2021 sigalor).
 * Format compact : BIC -> codes bancaires (nombres ou plages « début-fin »).
 */
const RANGES: Record<string, string> = {
  ABERBE22: '111',
  ABNABE2AIDJ: '674',
  ABNABE2AIPC: '509,513,722,960',
  ABNABE2AXXX: '719',
  ADIABE22: '640',
  ARSPBE22: '973,978-980',
  AXABBE22: '700-709,750-774,800-816,963,975',
  BARCBEBB: '653',
  BBRUBEBB: '185,300-399,630-631,652,673,828,880-881,883-884,887-888,910,920,922-923,929-931,934,936,939,961,971,976',
  BBVABEBB: '642',
  BCDMBEBB: '664',
  BCMCBEBB: '150',
  BIBLBE21: '587',
  BKCHBEBB: '605',
  BKIDBE22: '541',
  BLUXBEBB: '823',
  BMEUBEB1: '663',
  BMPBBEBB: '643',
  BMPBBEBBVOD: '648',
  BNAGBEBB: '132,634-636,646-647,871,877-879',
  BOFABE3X: '685-686,689',
  BOTKBEBX: '693',
  BSCHBEBB: '590-594',
  BSCHBEBBRET: '176',
  BYBBBEBB: '675',
  CBPXBE99: '677',
  CEKVBE88: '906,908',
  CEPABEB2: '649',
  CFFRBEB1: '586',
  CHASBEBX: '549',
  CITIBEBX: '570-579',
  CLIQBEB1: '940',
  CMCIBEB1BTB: '588',
  CMCIBEB1CIC: '667',
  CPHBBE75: '125-126,171',
  CREGBEBB: '190-199,728-729,732,742',
  CRLYBEBB: '696',
  CTBKBEBX: '109-110,113-114,119-124,127,129,131,133-134,595-601,950-959',
  CVMCBEBB: '941',
  DEGRBEBB: '583,676,845',
  DELEBE22: '678',
  DEUTBEBE: '610-613,694,825-826',
  DHBNBEBB: '501',
  DIERBE21: '507',
  DIGEBEB2: '130',
  DNIBBE21: '512',
  EBPBBEB1: '926',
  ENIBBEBB: '968',
  EPBFBEBB: '913',
  EURBBE99: '671',
  FMMSBEB1: '924',
  FPEBBEB2: '964',
  FVLBBE22: '521,525',
  FXBBBEBB: '914',
  GEBABEBB: '0-49,137,140-149,200-214,220-299',
  GKCCBEBB: '50-99,550-560,562-569,624-625,638,657,672,680,682-683,775-799,830-839',
  GOCFBEB1: '916',
  HABBBEBB: '658',
  HOMNBEB1: '948',
  HSBCBEBB: '949',
  ICBKBEBB: '607',
  IRVTBEBB: '515',
  ISAEBEBB: '817',
  JPMGBEBB: '945',
  JVBABE22: '645',
  KEYTBEBB: '651',
  KREDBEBB: '400-499,725-727,730-731,733-741,743-749,868',
  LOCYBEBB: '548',
  MBWMBEBB: '876',
  MGTCBEBE: '687,699',
  MHCBBEBB: '581',
  MTPSBEBB: '500',
  NBBEBEBB203: '100',
  NBBEBEBBHCC: '101',
  NICABEBB: '103-108,850-853,859-860,862-863,865-866',
  OONXBEBB: '915',
  PANXBEB1: '504',
  PARBBEBZMDC: '508',
  PAYVBEB2: '977',
  PCHQBEBB: '679,873,981-984',
  PESOBEB1: '974',
  PRIBBEBB: '840',
  PUILBEBB: '514,942,969',
  RCBPBEBB: '585',
  REVOBEB2XXX: '650',
  SBINBE2X: '668',
  SGABBEB2: '684,688',
  SMBCBEBB: '189',
  TRIOBEBB: '523',
  TRWIBEB1: '905,967',
  TUNZBEB1: '911',
  UTWBBEBB: '522',
  VAPEBE22: '510',
  VDSPBE91: '890-899',
  VPAYBEB1: '928',
  WAFABEBB: '524,546',
};

let table: Map<string, string> | null = null;

function lookup(): Map<string, string> {
  if (table) return table;
  table = new Map();
  for (const [bic, spec] of Object.entries(RANGES)) {
    for (const part of spec.split(',')) {
      const [from, to] = part.split('-').map(Number);
      for (let n = from; n <= (to ?? from); n++) table.set(String(n).padStart(3, '0'), bic);
    }
  }
  return table;
}

/** BIC proposé pour un IBAN belge (BE + 14 chiffres), sinon null. */
export function bicFromBelgianIban(iban: string | null | undefined): string | null {
  const s = (iban ?? '').replace(/\s+/g, '').toUpperCase();
  if (!/^BE\d{14}$/.test(s)) return null;
  return lookup().get(s.slice(4, 7)) ?? null;
}
